import { formatShortMonthDayYear } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import {
  CHANGE_OF_OWNERSHIP_PROJECTION,
  invalidateFacilityProfileQueries,
  useGetFacilityProfile,
} from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";
import {
  type ChangeOfOwnershipOriginWorkplace,
  type ChangeOfOwnershipUpdate,
  type CurrentChangeOfOwnership,
} from "@src/appV2/redesign/ChangeOfOwnership/types";
import { ChangeOfOwnershipDialog } from "@src/appV2/redesign/ChangeOfOwnership/components/ChangeOfOwnershipDialog";
import { useModalState } from "@src/lib/ui-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Typography } from "antd";
import { type ReactNode } from "react";

const { Text } = Typography;

// Resolving a workplace's name only needs name + userId. The origin fetch uses the shared
// `CHANGE_OF_OWNERSHIP_PROJECTION` so this control, the shift-migration dialog, and the NFC
// clone button all hit the same cache entry (see that constant's definition).
// The facility-profile API splits projections on spaces (see `loadsCustomProjection`), so this
// must be space-separated — a comma-joined value is treated as one (forbidden) field and 400s.
const NAME_PROJECTION = "name userId";

export interface ChangeOfOwnershipControlProps {
  origin: ChangeOfOwnershipOriginWorkplace;
  /**
   * Persists the change of ownership; `null` clears it. Rejects on failure so the dialog can
   * surface the error.
   */
  onSave: (changeOfOwnership: ChangeOfOwnershipUpdate) => Promise<void>;
  /** Sibling action buttons rendered in the same row as the record/update trigger. */
  children?: ReactNode;
}

/** Formats a stored ISO effective date in the facility's timezone, ignoring invalid values. */
function formatEffectiveDate(effectiveDate: string, tmz: string): string | undefined {
  const parsed = new Date(effectiveDate);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }
  return formatShortMonthDayYear(parsed, { timeZone: tmz });
}

/** Narrows a fetched change-of-ownership to one that has both required fields set. */
function toCurrent(
  changeOfOwnership:
    | { replacementFacilityId?: string | null; effectiveDate?: string | null }
    | null
    | undefined
): CurrentChangeOfOwnership | undefined {
  if (
    isDefined(changeOfOwnership?.replacementFacilityId) &&
    isDefined(changeOfOwnership.effectiveDate)
  ) {
    return {
      replacementFacilityId: changeOfOwnership.replacementFacilityId,
      effectiveDate: changeOfOwnership.effectiveDate,
    };
  }
  return undefined;
}

/**
 * Facility-drawer control for the change-of-ownership field. Renders the current new owner
 * and effective date when set, plus a single trigger button — "Record Change-of-Ownership"
 * when unset, "Update Change-of-Ownership" when set — that opens the dialog. The trigger sits
 * in one row with the drawer's other actions, passed as `children`.
 *
 * Lives outside `redesign/` (alongside `CloneNfcTagsButton`) so the trigger can use an antd
 * `Button` matching the legacy facility drawer's other actions ("Migrate shifts", "Clone NFC
 * tags"). The dialog itself stays in `redesign/` and provides its own `BerlinThemeProvider`.
 *
 * The persisted value is read from the facility-profile API (not the drawer's legacy facility
 * payload) and re-fetched after a save, so it renders correctly on save and on reload.
 */
export function ChangeOfOwnershipControl(props: ChangeOfOwnershipControlProps) {
  const { origin, onSave, children } = props;
  const modalState = useModalState();
  const queryClient = useQueryClient();

  const { data: originProfile } = useGetFacilityProfile(origin.id, CHANGE_OF_OWNERSHIP_PROJECTION);
  const current = toCurrent(originProfile?.changeOfOwnership);

  const currentReplacementId = current?.replacementFacilityId;
  const { data: replacementProfile } = useGetFacilityProfile(
    currentReplacementId ?? "",
    NAME_PROJECTION,
    { enabled: isDefined(currentReplacementId) }
  );

  const effectiveDateLabel = isDefined(current)
    ? formatEffectiveDate(current.effectiveDate, origin.tmz)
    : undefined;

  const handleSave = async (changeOfOwnership: ChangeOfOwnershipUpdate) => {
    await onSave(changeOfOwnership);
    invalidateFacilityProfileQueries(queryClient, origin.id);
  };

  return (
    <Stack spacing={2}>
      {isDefined(current) && (
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={1}>
            <Text strong>New owner:</Text>
            <Text>{replacementProfile?.name ?? current.replacementFacilityId}</Text>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Text strong>Effective date:</Text>
            <Text>{effectiveDateLabel ?? "Invalid date"}</Text>
          </Stack>
        </Stack>
      )}

      <Stack direction="row" spacing={2} flexWrap="wrap">
        <Button type="default" onClick={modalState.openModal}>
          {isDefined(current) ? "Update Change-of-Ownership" : "Record Change-of-Ownership"}
        </Button>
        {children}
      </Stack>

      {modalState.modalIsOpen && (
        <ChangeOfOwnershipDialog
          modalState={modalState}
          origin={origin}
          current={current}
          onSave={handleSave}
        />
      )}
    </Stack>
  );
}
